import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import { CanceledError } from 'axios';
import { OrderDirection } from 'common/contracts/order-direction';
import PaginationParameters from 'common/contracts/pagination-parameters';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSession } from 'users/session/session-context';
import FeeScheduleTemplateAPIService from 'work/entities/fee-schedule-template/api/fee-schedule-template-api-service';
import FeeScheduleTemplateQuery, { FeeScheduleTemplateFilterParameters, FeeScheduleTemplateOrderField, FeeScheduleTemplateOrderParameters } from 'work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request';
import FeeScheduleTemplate from 'work/entities/fee-schedule-template/fee-schedule-template';
import Loader from "common/components/loader";

const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50)
}));
const ListItemTextStyled = styled(ListItemText)(({ theme }) => ({
  minWidth: '26ch',
  maxWidth: '26ch',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  padding: theme.spacing(0.25)
}));

type FeeScheduleExistingTemplateProps = {
  templates?: FeeScheduleTemplate[];
  onTemplateSelected: (template: FeeScheduleTemplate) => void;
};

export default function FeeScheduleExistingTemplate(props: Readonly<FeeScheduleExistingTemplateProps>) {
  const {
    templates,
    onTemplateSelected
  } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [existingTemplates, setExistingTemplates] = React.useState<FeeScheduleTemplate[]>([]);
  const [paginatedParameters, setPaginatedParameters] = React.useState<PaginationParameters>();

  const session = useSession();

  useEffect(() => {
    let abortController = new AbortController();
    if (templates) {
      setExistingTemplates(templates);
    } else {
      void getFeeScheduleTemplates(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    }
  }, []);

  async function getFeeScheduleTemplates(abortController: AbortController) {
    setIsLoading(true);
    try {
      const entityId = session?.currentEntity.id;
      if (!entityId) return;

      const filterParams = new FeeScheduleTemplateFilterParameters(entityId);

      const orderParameters = new FeeScheduleTemplateOrderParameters(
        FeeScheduleTemplateOrderField.Name,
        OrderDirection.Descending
      );

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
        filterParams,
        orderParameters,
        paginatedParameters ?? new PaginationParameters(0, 100)
      );

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      const templates =
        await feeScheduleService.getFeeScheduleTemplates(
          templateQuery,
          abortController
        );
      setExistingTemplates(templates.data);
    }
    catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar(
        "Failed to load existing fee schedule templates.",
        {
          variant: "error",
        }
      );
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return new PaginationParameters(prev.pageIndex, prev.pageSize);
        }
      });
      setIsLoading(false);
    }
  }

  return (
    <ListContainer>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          {(!existingTemplates || existingTemplates.length < 1) && (
            <Typography>No existing fee schedule templates</Typography>
          )}
          {existingTemplates.map(template => {
            return (
              <ListItem
                key={template.name}
                onClick={() => onTemplateSelected(template)}
                secondaryAction={
                  <Typography>
                    {`Created ${template.createdDate?.format('MM/DD/YY hh:mm A')}`}
                  </Typography>
                }
              >
                <Tooltip
                  title={`Select Template: ${template.name}`}
                  placement="top-start"
                  arrow
                >
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => onTemplateSelected(template)}

                  >
                    <ListItemTextStyled
                      primary={
                        template.name.length > 18 ?
                          template.name.substring(0, 18) + '...' :
                          template.name
                      }
                      secondary={
                        template.tags.map(tag => <Chip key={tag.value} label={tag.value} size="small" />)
                      }
                    />
                  </Button>
                </Tooltip>
              </ListItem>
            );
          })}
        </>
      )}
    </ListContainer>
  )
}
